import { createTheme } from '@mui/material/styles';

// Alpha color utilities
const createAlphaColor = (hex, alpha) => {
  // Convert 6-digit hex to rgba
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Common theme values
const commonThemeValues = {
  shape: {
    borderRadius: 12
  },
  spacing: 8,
  shadows: [
    'none',
    createAlphaColor('#0F172A', 0.04),
    createAlphaColor('#0F172A', 0.06),
    createAlphaColor('#0F172A', 0.08),
    createAlphaColor('#0F172A', 0.08),
    createAlphaColor('#0F172A', 0.12),
    createAlphaColor('#0F172A', 0.05),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.1),
    createAlphaColor('#0F172A', 0.15)
  ],
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em'
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em'
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em'
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em'
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.2
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.2
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      letterSpacing: '0.01em'
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.01em'
    },
    body1: {
      fontSize: '1rem',
      letterSpacing: '0.01em',
      lineHeight: 1.5
    },
    body2: {
      fontSize: '0.875rem',
      letterSpacing: '0.01em',
      lineHeight: 1.5
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.02em'
    }
  }
};

// Common component overrides
const commonComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      '*': {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0
      },
      html: {
        MozOsxFontSmoothing: 'grayscale',
        WebkitFontSmoothing: 'antialiased',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%'
      },
      body: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%'
      },
      '#root': {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        height: '100%',
        width: '100%'
      }
    }
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: `0px 2px 4px ${createAlphaColor('#0F172A', 0.06)}`
        },
        transition: 'all 0.2s ease-in-out'
      },
      sizeSmall: {
        padding: '6px 16px'
      },
      sizeMedium: {
        padding: '8px 20px'
      },
      sizeLarge: {
        padding: '10px 24px'
      },
      contained: {
        '&:hover': {
          boxShadow: `0px 4px 8px ${createAlphaColor('#0F172A', 0.15)}`
        }
      }
    }
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: `0px 1px 3px ${createAlphaColor('#0F172A', 0.05)}`,
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: `0px 4px 12px ${createAlphaColor('#0F172A', 0.08)}`
        }
      }
    }
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '24px'
      }
    }
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)'
            }
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '2px'
            }
          }
        }
      }
    }
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '16px',
        borderBottom: `1px solid ${createAlphaColor('#CBD5E1', 0.5)}`,
        color: '#0F172A'
      },
      head: {
        fontWeight: 600,
        color: '#0F172A',
        backgroundColor: '#F8FAFC'
      }
    }
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: createAlphaColor('#4F46E5', 0.02)
        }
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        boxShadow: '0px 1px 3px rgba(15, 23, 42, 0.05)'
      }
    }
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#F8FAFC'
        }
      }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#CBD5E1',
          transition: 'border-color 0.2s ease-in-out'
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#94A3B8'
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#4F46E5'
        }
      }
    }
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 42,
        height: 26,
        padding: 0
      },
      switchBase: {
        padding: 1,
        '&.Mui-checked': {
          transform: 'translateX(16px)',
          color: '#FFFFFF',
          '& + .MuiSwitch-track': {
            backgroundColor: '#4F46E5',
            opacity: 1,
            border: 'none'
          }
        }
      },
      track: {
        borderRadius: 13,
        border: '1px solid #CBD5E1',
        backgroundColor: '#F1F5F9',
        opacity: 1,
        transition: 'background-color 0.2s ease-in-out'
      },
      thumb: {
        width: 24,
        height: 24,
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
      }
    }
  }
};

// Common palette values with standardized alpha colors
const commonPalette = {
  primary: {
    main: '#4F46E5',
    light: '#6366F1',
    dark: '#4338CA',
    contrastText: '#FFFFFF'
  },
  secondary: {
    main: '#0EA5E9',
    light: '#38BDF8',
    dark: '#0284C7',
    contrastText: '#FFFFFF'
  },
  error: {
    main: '#E11D48',
    light: '#FB7185',
    dark: '#BE123C',
    contrastText: '#FFFFFF'
  },
  warning: {
    main: '#F59E0B',
    light: '#FBBF24',
    dark: '#D97706',
    contrastText: '#FFFFFF'
  },
  info: {
    main: '#0EA5E9',
    light: '#38BDF8',
    dark: '#0284C7',
    contrastText: '#FFFFFF'
  },
  success: {
    main: '#10B981',
    light: '#34D399',
    dark: '#059669',
    contrastText: '#FFFFFF'
  },
  grey: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
    A100: '#F1F5F9',
    A200: '#E2E8F0',
    A400: '#94A3B8',
    A700: '#334155'
  },
  text: {
    primary: '#0F172A',
    secondary: '#475569',
    disabled: '#94A3B8'
  },
  divider: createAlphaColor('#0F172A', 0.06),
  background: {
    paper: '#FFFFFF',
    default: '#F8FAFC'
  },
  action: {
    active: '#64748B',
    hover: createAlphaColor('#4F46E5', 0.04),
    selected: createAlphaColor('#4F46E5', 0.08),
    disabled: '#CBD5E1',
    disabledBackground: '#F1F5F9',
    focus: createAlphaColor('#4F46E5', 0.12)
  }
};

// Light theme configuration
export const lightTheme = createTheme({
  ...commonThemeValues,
  palette: {
    ...commonPalette,
    mode: 'light',
    background: {
      default: '#F9FAFC',
      paper: '#FFFFFF'
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
      disabled: '#9CA3AF'
    },
    divider: '#E5E7EB'
  },
  components: {
    ...commonComponents,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)'
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            backgroundColor: '#4F46E5'
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E2E8F0'
        },
        head: {
          backgroundColor: '#F8FAFC',
          color: '#475569'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: createAlphaColor('#4F46E5', 0.04)
          }
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F8FAFC',
          color: '#0F172A',
          '&:hover': {
            backgroundColor: '#F8FAFC'
          }
        }
      }
    }
  }
});

// Dark theme configuration
export const darkTheme = createTheme({
  ...commonThemeValues,
  palette: {
    ...commonPalette,
    mode: 'dark',
    background: {
      default: '#0F172A',
      paper: '#1E293B'
    },
    text: {
      primary: '#F1F5F9',
      secondary: '#94A3B8',
      disabled: '#475569'
    },
    divider: '#334155',
    action: {
      active: '#94A3B8',
      hover: 'rgba(148, 163, 184, 0.08)',
      selected: 'rgba(148, 163, 184, 0.16)',
      disabled: 'rgba(148, 163, 184, 0.3)',
      disabledBackground: 'rgba(148, 163, 184, 0.12)',
      focus: 'rgba(148, 163, 184, 0.12)'
    }
  },
  components: {
    ...commonComponents,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          backgroundColor: '#1E293B',
          borderColor: '#334155',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1E293B',
          '&.MuiDrawer-paper': {
            backgroundColor: '#1E293B',
            borderRight: '1px solid #334155'
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #334155'
        },
        head: {
          backgroundColor: '#0F172A',
          color: '#F1F5F9'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.04)'
          }
        }
      }
    },
    MuiTextField : {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderColor: '#475569'
          },
          '& .MuiOutlinedInput-root:hover': {
            borderColor: '#475569'
          },
          '& .MuiOutlinedInput-root.Mui-focused': {
            borderColor: '#475569'
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#0F172A'
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-track': {
            backgroundColor: '#475569'
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#1E293B',
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.4)'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#334155'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.08)'
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(99, 102, 241, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(99, 102, 241, 0.24)'
            }
          }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#94A3B8',
          '&.Mui-focused': {
            color: '#818CF8'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E293B',
          '&:hover': {
            backgroundColor: '#0F172A'
          },
          '&.Mui-focused': {
            backgroundColor: '#1E293B',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#818CF8'
            }
          },
          '& input::placeholder': {
            color: '#64748B'
          },
          '& textarea::placeholder': {
            color: '#64748B'
          },
          '&:-webkit-autofill': {
            WebkitBoxShadow: '0 0 0 100px #1E293B inset',
            WebkitTextFillColor: '#F1F5F9'
          }
        },
        notchedOutline: {
          borderColor: '#475569'
        }
      }
    }
  }
});

// For backward compatibility with existing code
export const theme = lightTheme;
